import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextualSpotlight } from './contextual-spotlight.js';
import { useContextualOnboarding } from '../../../hooks/use-contextual-onboarding.js';
import { CONTEXTUAL_SECTIONS } from '../../../config/contextual-onboarding-registry.js';
import { pushToast, dismissToast } from '../../../features/app/toast-store.js';

const TOAST_ID_PREFIX = 'contextual-onboarding-';

export interface ContextualOnboardingOverlayProps {
  sectionId: string;
}

export function ContextualOnboardingOverlay({ sectionId }: ContextualOnboardingOverlayProps) {
  const { active, currentStep, totalSteps, targetRect, dismiss, next } =
    useContextualOnboarding(sectionId);
  const { t } = useTranslation('onboarding');
  const toastIdRef = useRef<string | null>(null);

  const section = CONTEXTUAL_SECTIONS.find((s) => s.sectionId === sectionId);
  const step = section?.steps[currentStep];

  // Push/update toast when active (upsert avoids dismiss+re-push flicker)
  useEffect(() => {
    const toastId = `${TOAST_ID_PREFIX}${sectionId}`;

    if (!active || !step) {
      if (toastIdRef.current) {
        dismissToast(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    toastIdRef.current = toastId;
    pushToast({
      id: toastId,
      tone: 'onboarding',
      title: t(step.titleKey),
      description: t(step.descriptionKey),
      showNext: currentStep < totalSteps - 1,
      onNext: next,
      onDismiss: dismiss,
    });
  }, [active, step, currentStep, totalSteps, sectionId, t, next, dismiss]);

  // Dismiss toast on unmount only
  useEffect(() => {
    return () => {
      if (toastIdRef.current) {
        dismissToast(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, [sectionId]);

  if (!active) {
    return null;
  }

  return <ContextualSpotlight targetRect={targetRect} visible={active} />;
}
